import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Forms = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📝 CSS Forms</h1>

      <p className="tutorial-intro">
        Forms are a key part of user interaction. With CSS, you can enhance form
        elements such as <code>input</code>, <code>textarea</code>,{" "}
        <code>select</code>, and <code>button</code> to improve usability and
        visual appeal.
      </p>
      {/* ✅ Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          🔑 Key Points
        </h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            Use <code>padding</code> and <code>margin</code> to space out form
            fields.
          </li>
          <li>
            Apply <code>border-radius</code> for smoother, modern inputs.
          </li>
          <li>
            Use <code>:focus</code> to highlight active input fields.
          </li>
          <li>
            Style <code>labels</code> clearly for better accessibility.
          </li>
          <li>
            Use consistent <b>button styles</b> for form submission.
          </li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Basic Styled Form</h2>
        <p>Simple styling for text inputs and buttons.</p>

        <CodeCopyBlock
          code={`/* CSS */
input, select, textarea {
  width: 100%;
  padding: 10px;
  margin: 6px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
}

button {
  background-color: #2563eb;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
button:hover {
  background-color: #1e40af;
}

/* HTML */
<form>
  <input type="text" placeholder="Enter Name" />
  <input type="email" placeholder="Enter Email" />
  <button type="submit">Submit</button>
</form>`}
          language="css"
        />

        <form style={{ maxWidth: "350px" }}>
          <input
            type="text"
            placeholder="Enter Name"
            style={{
              width: "100%",
              padding: "10px",
              margin: "6px 0",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
          <input
            type="email"
            placeholder="Enter Email"
            style={{
              width: "100%",
              padding: "10px",
              margin: "6px 0",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
          <button
            type="submit"
            style={{
              backgroundColor: "#2563eb",
              color: "white",
              padding: "10px 15px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Submit
          </button>
        </form>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Focus Effect</h2>
        <p>Highlight input fields when the user focuses on them.</p>

        <CodeCopyBlock
          code={`/* CSS */
input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 5px #93c5fd;
}`}
          language="css"
        />

        <input
          type="text"
          placeholder="Focus me"
          style={{
            padding: "10px",
            marginTop: "8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#3b82f6";
            e.target.style.boxShadow = "0 0 5px #93c5fd";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#ccc";
            e.target.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Rounded & Inline Form</h2>
        <p>Inputs with rounded corners and inline form design.</p>

        <CodeCopyBlock
          code={`/* CSS */
input.rounded {
  border-radius: 50px;
  padding: 8px 15px;
  border: 1px solid #bbb;
}

.inline-form input {
  display: inline-block;
  width: auto;
  margin-right: 10px;
}`}
          language="css"
        />

        <div className="inline-form" style={{ marginTop: "10px" }}>
          <input
            type="text"
            placeholder="Username"
            className="rounded"
            style={{
              borderRadius: "50px",
              padding: "8px 15px",
              border: "1px solid #bbb",
              marginRight: "10px",
            }}
          />
          <button
            style={{
              backgroundColor: "#16a34a",
              color: "white",
              padding: "8px 15px",
              border: "none",
              borderRadius: "50px",
            }}
          >
            Go
          </button>
        </div>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 4: Responsive Form with Labels
        </h2>
        <p>A more structured form with labels.</p>

        <CodeCopyBlock
          code={`/* CSS */
.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
label {
  margin-bottom: 4px;
  font-weight: bold;
}
input, select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
}`}
          language="css"
        />

        <form style={{ maxWidth: "400px" }}>
          <div className="form-group" style={{ marginBottom: "12px" }}>
            <label>Name</label>
            <input
              type="text"
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: "12px" }}>
            <label>Gender</label>
            <select
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            >
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
          </div>
          <button
            style={{
              backgroundColor: "#f97316",
              color: "white",
              padding: "10px 15px",
              border: "none",
              borderRadius: "4px",
            }}
          >
            Submit
          </button>
        </form>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Card-Styled Login Form</h2>
        <p>A clean card layout for login forms.</p>

        <CodeCopyBlock
          code={`/* CSS */
.login-card {
  max-width: 300px;
  margin: auto;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  background: white;
}
.login-card input {
  width: 100%;
  margin: 8px 0;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
}
.login-card button {
  width: 100%;
  padding: 10px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
}`}
          language="css"
        />

        <div
          className="login-card"
          style={{
            maxWidth: "300px",
            margin: "20px auto",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            background: "white",
          }}
        >
          <h3 style={{ marginBottom: "12px" }}>Login</h3>
          <input type="text" placeholder="Username" />
          <input type="password" placeholder="Password" />
          <button>Login</button>
        </div>
      </div>
      {/* Example 6 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 6: Modern Glassmorphism Form
        </h2>
        <p>
          A stylish form with frosted-glass background, glowing inputs, and a
          gradient button. Great for modern websites.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
.modern-form {
  max-width: 350px;
  margin: auto;
  padding: 25px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 20px rgba(0,0,0,0.2);
}

.modern-form h3 {
  text-align: center;
  margin-bottom: 15px;
}

.modern-form input {
  width: 100%;
  padding: 12px;
  margin: 8px 0;
  border-radius: 8px;
  border: "1px solid rgba(204, 204, 204, 0.4)",
  background: rgba(255,255,255,0.1);
  color: #111;
  outline: none;
  transition: 0.3s;
}

.modern-form input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 8px rgba(59,130,246,0.6);
}

.modern-form button {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #2563eb, #06b6d4);
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.modern-form button:hover {
  transform: scale(1.05);
}`}
          language="css"
        />

        <div
          className="modern-form"
          style={{
            maxWidth: "350px",
            margin: "20px auto",
            padding: "25px",
            borderRadius: "16px",
            backdropFilter: "blur(10px)",
            background: "rgba(255,255,255,0.2)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
          }}
        >
          <h3 style={{ marginBottom: "15px" }}>Sign Up</h3>
          <input
            type="text"
            placeholder="Full Name"
            style={{
              width: "100%",
              padding: "12px",
              margin: "8px 0",
              borderRadius: "8px",
              border: "1px solid rgba(204, 204, 204, 0.4)",
              background: "rgba(255,255,255,0.1)",
              outline: "none",
            }}
          />
          <input
            type="email"
            placeholder="Email Address"
            style={{
              width: "100%",
              padding: "12px",
              margin: "8px 0",
              borderRadius: "8px",
              border: "1px solid rgba(204, 204, 204, 0.4)",
              background: "rgba(255,255,255,0.1)",
              outline: "none",
            }}
          />
          <input
            type="password"
            placeholder="Password"
            style={{
              width: "100%",
              padding: "12px",
              margin: "8px 0",
              borderRadius: "8px",
              border: "1px solid rgba(204, 204, 204, 0.4)",
              background: "rgba(255,255,255,0.1)",
              outline: "none",
            }}
          />
          <button
            style={{
              width: "100%",
              padding: "12px",
              border: "none",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #2563eb, #06b6d4)",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              transition: "transform 0.2s ease",
              marginTop: "10px",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.05)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Create Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSS_Forms;
